import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardAPI, alertsAPI } from "../lib/api";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import AIInsightsPanel from "../components/AIInsightsPanel";
import {
  Shield,
  Monitor,
  AlertTriangle,
  Users,
  CheckCircle,
  XCircle,
  ArrowRight,
  TrendingUp,
  Clock,
  Database,
  Key,
  Activity,
} from "lucide-react";

export const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, alertsRes] = await Promise.all([
        dashboardAPI.getStats(),
        alertsAPI.getAll(),
      ]);
      setStats(statsRes);
      setAlerts(alertsRes.items.slice(0, 5));
    } catch (err) {
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "#10B981";
    if (score >= 60) return "#F59E0B";
    return "#EF4444";
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical":
        return "bg-red-100 text-red-800 border-red-200";
      case "warning":
        return "bg-amber-100 text-amber-800 border-amber-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="dashboard-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const scoreColor = getScoreColor(stats?.security_score || 0);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
          Dashboard
        </h1>
        <p className="text-muted-foreground mt-1">
          Overview of your security posture and IT infrastructure
        </p>
      </div>

      {/* Bento Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-12 gap-6">
        {/* Security Score - Large Card */}
        <Card
          className="md:col-span-2 lg:col-span-4 lg:row-span-2 border-border shadow-sm card-hover"
          data-testid="security-score-card"
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Shield className="w-4 h-4" />
              SECURITY SCORE
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center pt-4">
            <div className="security-score-circle">
              <svg width="180" height="180" viewBox="0 0 180 180">
                <circle
                  cx="90"
                  cy="90"
                  r="80"
                  fill="none"
                  stroke="#E2E8F0"
                  strokeWidth="12"
                />
                <circle
                  cx="90"
                  cy="90"
                  r="80"
                  fill="none"
                  stroke={scoreColor}
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={`${(stats?.security_score || 0) * 5.02} 502`}
                />
              </svg>
              <div className="score-value">
                <span
                  className="font-heading text-5xl font-bold"
                  style={{ color: scoreColor }}
                >
                  {stats?.security_score || 0}
                </span>
                <p className="text-sm text-muted-foreground mt-1">out of 100</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span>Live monitoring active</span>
            </div>
          </CardContent>
        </Card>

        {/* Device Stats */}
        <Card
          className="md:col-span-2 lg:col-span-4 border-border shadow-sm card-hover"
          data-testid="devices-stat-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              DEVICES
            </CardTitle>
            <Monitor className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-4xl font-bold">
              {stats?.total_devices || 0}
            </div>
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1.5">
                <span className="status-dot status-online"></span>
                <span className="text-sm text-muted-foreground">
                  {stats?.online_devices || 0} online
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="status-dot status-offline"></span>
                <span className="text-sm text-muted-foreground">
                  {stats?.offline_devices || 0} offline
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              className="mt-4 p-0 h-auto text-sm text-primary"
              onClick={() => navigate("/devices")}
              data-testid="view-devices-btn"
            >
              View all devices <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Alert Stats */}
        <Card
          className="md:col-span-2 lg:col-span-4 border-border shadow-sm card-hover"
          data-testid="alerts-stat-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              OPEN ALERTS
            </CardTitle>
            <AlertTriangle className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-4xl font-bold">
              {(stats?.critical_alerts || 0) +
                (stats?.warning_alerts || 0) +
                (stats?.info_alerts || 0)}
            </div>
            <div className="flex items-center gap-3 mt-3">
              <Badge variant="destructive" className="rounded-full">
                {stats?.critical_alerts || 0} critical
              </Badge>
              <Badge className="rounded-full bg-amber-100 text-amber-800 border-amber-200">
                {stats?.warning_alerts || 0} warning
              </Badge>
            </div>
            <Button
              variant="ghost"
              className="mt-4 p-0 h-auto text-sm text-primary"
              onClick={() => navigate("/alerts")}
              data-testid="view-alerts-btn"
            >
              View all alerts <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Users Stat */}
        <Card
          className="md:col-span-2 lg:col-span-4 border-border shadow-sm card-hover"
          data-testid="users-stat-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              USERS
            </CardTitle>
            <Users className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-4xl font-bold">
              {stats?.total_users || 0}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Active in your organization
            </p>
            <Button
              variant="ghost"
              className="mt-4 p-0 h-auto text-sm text-primary"
              onClick={() => navigate("/users")}
              data-testid="view-users-btn"
            >
              Manage users <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Compliance Score */}
        <Card
          className="md:col-span-2 lg:col-span-4 border-border shadow-sm card-hover"
          data-testid="compliance-stat-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              COMPLIANCE
            </CardTitle>
            <CheckCircle className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-4xl font-bold">
              {stats?.compliance_score || 0}%
            </div>
            <div className="compliance-bar mt-3">
              <div
                className="compliance-bar-fill"
                style={{
                  width: `${stats?.compliance_score || 0}%`,
                  backgroundColor: getScoreColor(stats?.compliance_score || 0),
                }}
              />
            </div>
            <Button
              variant="ghost"
              className="mt-4 p-0 h-auto text-sm text-primary"
              onClick={() => navigate("/compliance")}
              data-testid="view-compliance-btn"
            >
              View compliance <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Health Summary - New Cards */}
        <Card
          className={`md:col-span-2 lg:col-span-4 border-border shadow-sm card-hover ${(stats?.backup_failures || 0) > 0 ? "border-red-300" : ""}`}
          data-testid="backup-stat-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              BACKUPS
            </CardTitle>
            <Database
              className={`w-4 h-4 ${(stats?.backup_failures || 0) > 0 ? "text-red-500" : "text-muted-foreground"}`}
            />
          </CardHeader>
          <CardContent>
            {(stats?.backup_failures || 0) > 0 ? (
              <>
                <div className="font-heading text-4xl font-bold text-red-600">
                  {stats?.backup_failures} Failed
                </div>
                <p className="text-sm text-red-600 mt-1">
                  Immediate attention required
                </p>
              </>
            ) : (
              <>
                <div className="font-heading text-4xl font-bold text-green-600">
                  All Good
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  All backups successful
                </p>
              </>
            )}
            <Button
              variant="ghost"
              className="mt-4 p-0 h-auto text-sm text-primary"
              onClick={() => navigate("/health")}
              data-testid="view-backups-btn"
            >
              View backups <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        <Card
          className={`md:col-span-2 lg:col-span-4 border-border shadow-sm card-hover ${(stats?.passwords_at_risk || 0) > 0 ? "border-red-300" : ""}`}
          data-testid="password-stat-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              PASSWORD HEALTH
            </CardTitle>
            <Key
              className={`w-4 h-4 ${(stats?.passwords_at_risk || 0) > 0 ? "text-red-500" : "text-muted-foreground"}`}
            />
          </CardHeader>
          <CardContent>
            {(stats?.passwords_at_risk || 0) > 0 ? (
              <>
                <div className="font-heading text-4xl font-bold text-red-600">
                  {stats?.passwords_at_risk} At Risk
                </div>
                <p className="text-sm text-red-600 mt-1">
                  Leaked or weak passwords
                </p>
              </>
            ) : (
              <>
                <div className="font-heading text-4xl font-bold text-green-600">
                  Secure
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  No passwords compromised
                </p>
              </>
            )}
            <Button
              variant="ghost"
              className="mt-4 p-0 h-auto text-sm text-primary"
              onClick={() => navigate("/health")}
              data-testid="view-passwords-btn"
            >
              View password health <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        <Card
          className={`md:col-span-2 lg:col-span-4 border-border shadow-sm card-hover ${(stats?.devices_unhealthy || 0) > 0 ? "border-amber-300" : ""}`}
          data-testid="device-health-stat-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              DEVICE HEALTH
            </CardTitle>
            <Activity
              className={`w-4 h-4 ${(stats?.devices_unhealthy || 0) > 0 ? "text-amber-500" : "text-muted-foreground"}`}
            />
          </CardHeader>
          <CardContent>
            {(stats?.devices_unhealthy || 0) > 0 ? (
              <>
                <div className="font-heading text-4xl font-bold text-amber-600">
                  {stats?.devices_unhealthy} Issues
                </div>
                <p className="text-sm text-amber-600 mt-1">
                  Devices need attention
                </p>
              </>
            ) : (
              <>
                <div className="font-heading text-4xl font-bold text-green-600">
                  Healthy
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  All devices running well
                </p>
              </>
            )}
            <Button
              variant="ghost"
              className="mt-4 p-0 h-auto text-sm text-primary"
              onClick={() => navigate("/health")}
              data-testid="view-device-health-btn"
            >
              View device health <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Recent Alerts */}
        <Card
          className="md:col-span-4 lg:col-span-8 border-border shadow-sm"
          data-testid="recent-alerts-card"
        >
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg font-semibold">
              Recent Alerts
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/alerts")}
              data-testid="view-all-alerts-btn"
            >
              View all
            </Button>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mb-3" />
                <p>No open alerts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="flex items-start gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    data-testid={`alert-item-${alert.id}`}
                  >
                    <div
                      className={`p-2 rounded-full ${
                        alert.severity === "critical"
                          ? "bg-red-100"
                          : alert.severity === "warning"
                            ? "bg-amber-100"
                            : "bg-blue-100"
                      }`}
                    >
                      {alert.severity === "critical" ? (
                        <XCircle className="w-4 h-4 text-red-600" />
                      ) : (
                        <AlertTriangle
                          className={`w-4 h-4 ${
                            alert.severity === "warning"
                              ? "text-amber-600"
                              : "text-blue-600"
                          }`}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">
                          {alert.title}
                        </p>
                        <Badge
                          className={`${getSeverityColor(alert.severity)} text-xs`}
                        >
                          {alert.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate mt-0.5">
                        {alert.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                      <Clock className="w-3 h-3" />
                      {formatTimeAgo(alert.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Insights Section */}
      <div className="mt-8">
        <AIInsightsPanel />
      </div>
    </div>
  );
};

export default DashboardPage;
